import { useAuth0 } from '@auth0/auth0-react'
import { usePathname, useSearchParams } from 'next/navigation'
import { type PropsWithChildren, useEffect, useMemo, useState } from 'react'
import { useRef } from 'react'

import AuthWithToken from '~/app/auth/oauth/page'
import ThemeComponent from '@core/theme/ThemeComponent'

import { LoadingMessage } from '~/components/Loaders'
import { useBootMark } from '~/debug/bootProfiler'
import useLogger from '~/debug/logger'
import useFirstRender from '~/hooks/useFirstRender'
import { useLocale } from '~/store/slices/display'
import { useTenantInfo } from '~/store/slices/tenant'

import { AuthProvider } from './AuthProvider'
import getCustomerAssets from './assets'

type AuthenticatorWrapperProps = PropsWithChildren<{
  onConfirmedUnauthenticated: () => void
}>

/**
 * The app's ThemeComponent is mounted below this wrapper, so the loaders it
 * shows while auth settles would otherwise render with no theme at all: no
 * body background and MUI's default black text. In a browser that happens to
 * look fine on the white page; in the desktop shell, whose window is dark,
 * it is black on near-black. The settings provider above us has already
 * restored the stored light/dark preference, so theming the loader here
 * paints the body in the right colour too.
 */
function BootLoading() {
  return (
    <ThemeComponent>
      <LoadingMessage />
    </ThemeComponent>
  )
}

export default function AuthenticatorWrapper({ children, onConfirmedUnauthenticated }: AuthenticatorWrapperProps) {
  // Add this flag to check if the Refresh flag is set by the sso flow
  // So we can refresh and pick up the currently logged in user before
  // showing them an unnessecary log in screen
  const [shouldRender, setShouldRender] = useState(false)
  useFirstRender(() => {
    const userData = window.localStorage.getItem('userData')
    if (userData === 'refresh') {
      window.localStorage.removeItem('userData')
      window.location.reload()
    } else {
      setShouldRender(true)
    }
  })

  useBootMark({ phase: 'auth-wrapper-passed', when: shouldRender })

  if (!shouldRender) return <BootLoading />
  return <Auth0Wrapper onConfirmedUnauthenticated={onConfirmedUnauthenticated}>{children}</Auth0Wrapper>
}

function Auth0Wrapper({ children, onConfirmedUnauthenticated }: AuthenticatorWrapperProps) {
  const logger = useLogger('Auth0Wrapper')
  const pathname = usePathname()
  const { user: auth0User, isLoading: isUserLoading, loginWithRedirect } = useAuth0()
  useBootMark({ phase: 'auth0-loaded', when: !isUserLoading })
  const tenantInfo = useTenantInfo()
  const locale = useLocale()
  const customerAssets = getCustomerAssets()

  // If Auth0 confirms the user is unauthenticated, notify the parent to clear IndexedDB
  const confirmedUnauthenticated = !isUserLoading && !auth0User
  const onConfirmedUnauthenticatedRef = useRef(onConfirmedUnauthenticated)
  useEffect(() => {
    if (confirmedUnauthenticated) {
      onConfirmedUnauthenticatedRef.current()
    }
  }, [confirmedUnauthenticated])

  // If there is an error set on the user's auth0 session we need to clear it.
  const searchParams = useSearchParams()
  const errorParam = searchParams?.get('error')
  const { logout } = useAuth0()
  useFirstRender(() => {
    if (errorParam) logout()
  })

  const authorizationParams = useMemo(() => {
    const isPlusAliased = tenantInfo?.email?.includes('+') ?? false
    const baseParams = {
      login_hint: tenantInfo?.email || undefined,
      ui_locales: locale ?? 'en-US'
    }
    // An SSO button's connection wins over the password connection a host forces for its email path.
    if (tenantInfo?.connection) return { ...baseParams, connection: tenantInfo.connection }
    return isPlusAliased || customerAssets?.forcePasswordConnection
      ? {
          ...baseParams,
          connection: 'Username-Password-Authentication'
        }
      : baseParams
  }, [tenantInfo?.email, tenantInfo?.connection, locale, customerAssets])

  useEffect(() => {
    // When you use the back button on the universal login page it brings
    // you back to the app but loads the page from the bfcache which causes
    // the below redirect logic not to fire. This will check if the page
    // was restored from the bf cache and redirect to login again.
    const handlePageShow = (event: PageTransitionEvent) => {
      if (event.persisted) {
        logger.info('This page was restored from the bfcache.')
        if (confirmedUnauthenticated && !pathname.includes('auth/oauth')) {
          loginWithRedirect({
            authorizationParams
          })
        }
      }
    }

    window.addEventListener('pageshow', handlePageShow)
    return () => window.removeEventListener('pageshow', handlePageShow)
  }, [confirmedUnauthenticated, loginWithRedirect, pathname, logger, authorizationParams])

  // auth/oauth path is used by a customer as the unauthenticated route they hit with their
  // JWT, this route will initiate a login with a custom oauth server on our backend
  // and log the user in provided their JWT is valid with their's user claims endpoint
  if (pathname.includes('auth/oauth')) return <AuthWithToken />

  // If there is no user yet and we are not loading the user still, redirect to login
  if (!auth0User) {
    if (!isUserLoading) {
      loginWithRedirect({
        authorizationParams
      })
    }
    return <BootLoading />
  }

  // First try to get userId from internal_id, otherwise fall back to sub parsing
  // Auth0 subs come in a format like "Auth0|maybe_connection_name|userId"
  const userIdFromMetadata = auth0User.internal_id
  const splitUserSub = auth0User.sub?.split('|')
  const userIdFromSub = splitUserSub?.at(-1) ?? ''
  const userId = userIdFromMetadata || userIdFromSub
  const user = {
    username: auth0User.email ?? '',
    userId
  }

  return <AuthProvider user={user}>{children}</AuthProvider>
}
